// Angular
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Data } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

// Snagjaob
import { SAJScriptService } from '@snagajob/angular/services/script';
import { AdobeEmployerUser, AdobeEvent } from '@snagajob/adobe';

// Libs
import { APP_CONFIG } from '@libs/configs';
import {
  AuthenticationService,
  MarketplacePermission,
} from '@libs/authentication/services';
import { App, ReportingPageName } from '@libs/types';

// Self
import { RouteContextService } from './route-context.service';
import { isPlatformBrowser } from '@angular/common';

class Click {
  constructor(
    public readonly pageName: string,
    public readonly pageSection: string,
    public readonly target: string,
    public readonly context?: any,
  ) {}
}

class PageView {
  constructor(
    public readonly pageName: ReportingPageName,
    public readonly context?: any,
  ) {}
}

class FormStarted {
  constructor(
    public readonly pageName: string,
    public readonly formName: string,
    public readonly context?: any,
  ) {}
}

class FormSubmitted {
  constructor(
    public readonly pageName: string,
    public readonly formName: string,
    public readonly context?: any,
  ) {}
}

type AccountType = 'ecomm' | 'enterprise';

/**
 * See how to use this service here: https://snagajob.atlassian.net/wiki/spaces/EN/pages/2842853391/Adobe+How-To
 */
@Injectable({
  providedIn: 'root',
})
export class AdobeService {
  private adobeLoaded = false;
  // processes all AdobeEvents once a User is logged in
  private adobeUserEventProcessor$: Subject<
    Click | PageView | Partial<AdobeEvent>
  > = new Subject();

  private user: AdobeEmployerUser = {
    uuid: 'unknown',
    loggedIn: false,
    userType: 'employer',
    accountType: '',
  };

  currentPageName: ReportingPageName;

  constructor(
    private script: SAJScriptService,
    private authService: AuthenticationService,
    private routeContextService: RouteContextService,
    @Inject(APP_CONFIG) private readonly appConfig,
    @Inject(PLATFORM_ID) platformId: any,
  ) {}

  private initializeAdobeEventAndAuthListener(): void {
    return;
  }

  configureAdobeForUser(
    isImpersonatedUser: boolean,
    accountId: string,
    memberId: string,
    roles: string[],
    loggedIn: boolean,
    isHubSelfSignup: boolean,
  ): void {
    this.user = {
      uuid: `${memberId}_${accountId}`,
      loggedIn: loggedIn,
      userType: 'employer',
      roleName: this.determineRoleName(roles, isImpersonatedUser),
      accountId: accountId,
      memberId: memberId,
      accountType: this.determineAccountType(isHubSelfSignup),
    };
    this.setAdobeUserAndCommitNumber();

    this.trackEvent({
      event: 'login-successful',
      page: {
        name: 'global',
      },
    });
  }

  setupAdobeLaunchScript(): void {
    return;
  }

  private setPageContext(): void {
    this.routeContextService
      .getActivatedRouteData()
      .pipe(
        filter((data: Data) => data?.['reportingPageName']),
        map((data: Data) => data['reportingPageName'] as ReportingPageName),
      )
      .subscribe((reportingPageName: ReportingPageName) => {
        if (reportingPageName !== this.currentPageName) {
          this.currentPageName = reportingPageName;
          this.trackPageView(this.currentPageName);
        }
      });
  }

  trackPageView(pageName: ReportingPageName, context?: any): void {
    this.adobeUserEventProcessor$.next(new PageView(pageName, context));
  }

  trackClick(pageSection: string, target: string, context?: any): void {
    this.adobeUserEventProcessor$.next(
      new Click(this.currentPageName, pageSection, target, context),
    );
  }

  trackFormStarted(formName: string, context?: any): void {
    this.adobeUserEventProcessor$.next(
      new FormStarted(this.currentPageName, formName, context),
    );
  }

  trackFormSubmitted(formName: string, context?: any): void {
    this.adobeUserEventProcessor$.next(
      new FormSubmitted(this.currentPageName, formName, context),
    );
  }

  trackEvent(event: Partial<AdobeEvent>) {
    this.adobeUserEventProcessor$.next(event);
  }

  /**
   * Use this method if you want to track a click before a user logs in
   */
  trackPreLoginClick(pageSection: string, target: string, context?: any): void {
    this.fireEvent(
      new Click(this.currentPageName, pageSection, target, context),
    );
  }

  /**
   * Use this method if you want to track a pageview before a user logs in
   */
  trackPreLoginPageView(pageName: ReportingPageName, context?: any): void {
    this.fireEvent(new PageView(pageName, context));
  }

  /**
   * Use this method if you want to track an event before a user logs in
   */
  trackPreLoginEvent(event: Partial<AdobeEvent>) {
    this.fireEvent(event);
  }

  private fireEvent(adobeEvent: Partial<AdobeEvent>): void {
    return;
  }

  /**
   * Web.Core needs a User and Commit to be set before it can log events. Initialize this immediately
   * after Adobe script is loaded, then update User once they are logged in
   */
  private setAdobeUserAndCommitNumber(): void {
    return;
  }

  private determineRoleName(
    roles: string[],
    isImpersonatedUser: boolean,
  ): string {
    if (isImpersonatedUser) {
      return 'SAJInternalUser';
    } else if (roles?.length > 0) {
      return roles[0];
    } else {
      return null;
    }
  }

  private determineAccountType(isEcommSelfSignup?: boolean): AccountType {
    return isEcommSelfSignup ? 'ecomm' : 'enterprise';
  }
}
